import { makeStyles } from '@material-ui/core/styles'

/** Spacing on Refactoring UI scale: 4 / 8 / 16 / 24 / 32 */
const useStyles = makeStyles(theme => ({
  form: {
    '& .MuiFormLabel-asterisk': {
      color: theme.palette.error.main,
    },
    '& .MuiInputLabel-asterisk': {
      color: theme.palette.error.main,
    },
    '& .MuiFormHelperText-root': {
      width: '100%',
    },
  },
  requiredAsterisk: {
    color: theme.palette.error.main,
  },
  fieldHelperBlock: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 16,
    width: '100%',
  },
  fieldHelperHint: {
    flex: 1,
    minWidth: 0,
  },
  charCount: {
    flexShrink: 0,
    marginLeft: 'auto',
    color: theme.palette.text.secondary,
    fontSize: 12,
    lineHeight: 1.5,
    textAlign: 'right',
    whiteSpace: 'nowrap',
  },
  fullWidth: {
    width: '100%',
  },
  tabs: {
    marginBottom: 16,
    minHeight: 40,
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  tabPanel: {
    minHeight: 320,
    width: '100%',
    paddingTop: 16,
  },
  sectionHint: {
    color: theme.palette.text.secondary,
    fontSize: 14,
    lineHeight: 1.5,
    marginBottom: 24,
  },
  fieldHint: {
    color: theme.palette.text.secondary,
    fontSize: 12,
    lineHeight: 1.5,
    marginTop: 8,
  },
  mediaFieldHint: {
    color: theme.palette.text.secondary,
    fontSize: 12,
    lineHeight: 1.5,
    marginBottom: 8,
  },
  sectionTitle: {
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: theme.palette.text.secondary,
    marginBottom: 8,
  },
  sectionTitleError: {
    color: theme.palette.error.main,
  },
  sectionBlock: {
    marginTop: 24,
    userSelect: 'none',
  },
  hiddenUnlockFields: {
    visibility: 'hidden',
    pointerEvents: 'none',
  },
  tabLabelWithError: {
    position: 'relative',
    display: 'inline-block',
    paddingRight: 12,
  },
  tabErrorDot: {
    position: 'absolute',
    top: 2,
    right: 0,
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: theme.palette.error.main,
    display: 'block',
    flexShrink: 0,
  },
  mediaRow: {
    display: 'flex',
    gap: 24,
    width: '100%',
    marginTop: 8,
  },
  mediaSlot: {
    flex: 1,
    minWidth: 0,
  },
  mediaPreview: {
    marginTop: 16,
    width: 96,
    height: 96,
    borderRadius: 8,
    overflow: 'hidden',
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.grey[50],
  },
  mediaPreviewImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
  },
  multiselectTitle: {
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: theme.palette.text.secondary,
    marginBottom: 8,
  },
  multiselectTitleError: {
    color: theme.palette.error.main,
  },
  selectionField: {
    width: '100%',
    borderBottom: '1px solid rgba(0, 0, 0, 0.42)',
    transition: 'border-bottom-color 200ms cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
      borderBottom: '2px solid rgba(0, 0, 0, 0.87)',
      marginBottom: -1,
    },
    '&:focus-within': {
      borderBottom: `2px solid ${theme.palette.text.primary}`,
      marginBottom: -1,
    },
  },
  fieldError: {
    color: theme.palette.error.main,
    fontSize: 12,
    lineHeight: 1.5,
    marginTop: 8,
  },
  selectionChips: {
    maxHeight: 120,
    overflowY: 'auto',
    display: 'flex',
    flexWrap: 'wrap',
    gap: 0,
  },
  selectionChip: {
    height: 24,
    marginTop: 4,
    marginBottom: 4,
    marginRight: 8,
  },
  selectionChipInactive: {
    height: 24,
    marginTop: 4,
    marginBottom: 4,
    marginRight: 8,
    backgroundColor: theme.palette.grey[500],
    color: theme.palette.common.white,
    '& .MuiChip-deleteIcon': {
      color: theme.palette.common.white,
    },
  },
  multiSelectPlaceholder: {
    color: theme.palette.text.secondary,
    fontSize: 14,
  },
  multiSelectArrow: {
    color: 'rgba(0, 0, 0, 0.54)',
    fontSize: 24,
  },
  multiSelectChild: {
    '--rmsc-h': '32px',
    '& > div': {
      border: 'none !important',
      borderRadius: '0 !important',
      boxShadow: 'none !important',
    },
    '&:focus-within > div': {
      border: 'none !important',
      boxShadow: 'none !important',
    },
    '& .dropdown-heading': {
      padding: '0 !important',
      height: '32px !important',
      minHeight: '32px !important',
    },
    '& .dropdown-heading-dropdown-arrow': {
      display: 'flex',
      alignItems: 'center',
    },
    '& .dropdown-container': {
      boxShadow: 'none !important',
      outline: 'none !important',
      border: 'none !important',
      overflow: 'visible',
    },
    '& .dropdown-content': {
      zIndex: 2,
    },
    '& .clear-selected-button': {
      color: 'rgba(0, 0, 0, 0.54)',
    },
  },
  levelsList: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: 8,
    userSelect: 'none',
  },
  levelRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 16,
    minHeight: 48,
    padding: '8px 16px',
    marginBottom: 16,
    borderRadius: 8,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.common.white,
    cursor: 'pointer',
    userSelect: 'none',
    '&:last-child': {
      marginBottom: 0,
    },
    '&:hover': {
      backgroundColor: theme.palette.grey[50],
    },
  },
  levelRowActive: {
    borderColor: theme.palette.grey[400],
    backgroundColor: theme.palette.grey[50],
  },
  levelCheckbox: {
    marginRight: 0,
    pointerEvents: 'none',
    userSelect: 'none',
  },
  levelDuration: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: 16,
    flexShrink: 0,
  },
  levelDurationUnit: {
    width: 88,
    minWidth: 88,
  },
  levelMinutesHidden: {
    visibility: 'hidden',
    pointerEvents: 'none',
  },
  levelMinutesInteractive: {
    cursor: 'text',
    userSelect: 'text',
  },
  activeToggle: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'flex-end',
  },
  activeControl: {
    width: 'auto',
  },
  activeLabel: {
    fontSize: 12,
    marginBottom: 0,
    lineHeight: 1.2,
  },
  actionsActiveToggle: {
    margin: 0,
    '& .MuiFormControlLabel-label': {
      fontSize: 12,
      color: theme.palette.text.secondary,
      marginRight: 4,
    },
  },
  orderDialogBody: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: 0,
    width: '100%',
  },
  orderHint: {
    color: theme.palette.text.secondary,
    fontSize: 14,
    lineHeight: 1.5,
    marginBottom: 16,
    flexShrink: 0,
    width: '100%',
  },
  orderPreview: {
    width: '100%',
    backgroundColor: '#F6F5F3',
    borderRadius: 16,
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  orderPreviewTitle: {
    fontFamily: "'Raleway', sans-serif",
    fontSize: 19,
    fontWeight: 600,
    lineHeight: 1.2,
    color: '#1A171B',
    margin: 0,
    padding: '30px 20px 0',
  },
  orderLevelTabs: {
    display: 'flex',
    flexWrap: 'nowrap',
    padding: '20px 20px 0',
    overflowX: 'auto',
    flexShrink: 0,
  },
  orderLevelTab: {
    display: 'flex',
    alignItems: 'center',
    height: 32,
    padding: '0 10px',
    marginRight: 10,
    borderRadius: 8,
    border: '1px solid #1A171B',
    backgroundColor: 'transparent',
    fontFamily: "'Raleway', sans-serif",
    fontSize: 14.5,
    fontWeight: 600,
    lineHeight: '16px',
    appearance: 'none',
    WebkitAppearance: 'none',
    cursor: 'pointer',
    color: '#1A171B',
    whiteSpace: 'nowrap',
    flexShrink: 0,
    '&:last-child': {
      marginRight: 0,
    },
  },
  orderLevelTabIcon: {
    display: 'block',
    flexShrink: 0,
    marginRight: 10,
  },
  orderLevelTabSelected: {
    borderColor: '#E0061A',
    color: '#E0061A',
  },
  orderBenefits: {
    display: 'flex',
    flexDirection: 'column',
    padding: '10px 20px 24px',
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
  },
  orderBenefit: {
    display: 'flex',
    alignItems: 'center',
    margin: '10px 0',
    backgroundColor: 'transparent',
    cursor: 'grab',
    userSelect: 'none',
  },
  orderBenefitDragging: {
    backgroundColor: '#EFEDE9',
    borderRadius: 8,
    boxShadow: '0 2px 8px rgba(26, 23, 27, 0.12)',
    cursor: 'grabbing',
  },
  orderDragHandle: {
    display: 'inline-flex',
    color: '#1A171B',
    flexShrink: 0,
    marginLeft: 'auto',
    opacity: 0.45,
  },
  orderBenefitIcon: {
    width: 26,
    height: 26,
    objectFit: 'contain',
    flexShrink: 0,
    marginRight: 15,
  },
  orderBenefitIconPlaceholder: {
    width: 26,
    height: 26,
    flexShrink: 0,
    marginRight: 15,
  },
  orderBenefitBody: {
    minWidth: 0,
    flex: 1,
    paddingRight: 8,
  },
  orderBenefitTitle: {
    display: 'block',
    fontFamily: "'Raleway', sans-serif",
    fontSize: 16,
    fontWeight: 600,
    lineHeight: 1.2,
    color: '#1A171B',
    '& p': {
      margin: 0,
      font: 'inherit',
    },
  },
  orderBenefitSubtitle: {
    display: 'block',
    fontFamily: "'Raleway', sans-serif",
    fontSize: 14.5,
    fontWeight: 400,
    lineHeight: 1.3,
    color: '#1A171B',
    marginTop: 2,
  },
  orderEmpty: {
    fontFamily: "'Raleway', sans-serif",
    color: '#9a9a9a',
    fontSize: 14.5,
    lineHeight: 1.5,
    margin: 0,
    padding: '24px 0',
    textAlign: 'center',
  },
}))

export { useStyles }
